import { useState, useEffect, useCallback } from 'react';
import { communityApi } from './communityApi';
import { toast } from 'sonner';
import { Heart, MessageCircle, Send, Trash2, Loader2, ChevronDown, ChevronUp, Search, HelpCircle } from 'lucide-react';

const MiniAvatar = ({ username }) => {
  const colors = ['from-emerald-500/40 to-emerald-900/40', 'from-blue-500/40 to-blue-900/40', 'from-purple-500/40 to-purple-900/40', 'from-amber-500/40 to-amber-900/40', 'from-red-500/40 to-red-900/40', 'from-cyan-500/40 to-cyan-900/40'];
  const idx = (username || '').charCodeAt(0) % colors.length;
  return (
    <div className={`w-8 h-8 rounded-full bg-gradient-to-br ${colors[idx]} flex items-center justify-center font-display font-bold text-white text-xs border border-white/10 flex-shrink-0`}>
      {(username || '?')[0]}
    </div>
  );
};

const API_URL = process.env.REACT_APP_BACKEND_URL;

const timeSince = (dateStr) => {
  const seconds = Math.floor((Date.now() - new Date(dateStr).getTime()) / 1000);
  if (seconds < 60) return "à l'instant";
  const mins = Math.floor(seconds / 60);
  if (mins < 60) return `il y a ${mins}min`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `il y a ${hours}h`;
  const days = Math.floor(hours / 24);
  return days < 7 ? `il y a ${days}j` : new Date(dateStr).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });
};

// ── Answer row ──
const AnswerRow = ({ answer, onLike }) => (
  <div className="flex gap-2.5 py-2.5 ml-10 border-l border-white/5 pl-4">
    <MiniAvatar username={answer.author} />
    <div className="flex-1 min-w-0">
      <div className="flex items-center gap-2 mb-0.5">
        <span className="text-white text-xs font-semibold">{answer.author}</span>
        {answer.is_owner && <span className="text-[8px] px-1 py-0.5 rounded bg-[#00e5a0]/10 text-[#00e5a0] font-bold">VOUS</span>}
        <span className="text-cap-text-muted text-[10px]">{timeSince(answer.created_at)}</span>
      </div>
      <p className="text-cap-text-secondary text-sm">{answer.content}</p>
      <button onClick={() => onLike(answer.id)}
        className={`flex items-center gap-1 text-[11px] mt-1 transition-colors ${answer.user_liked ? 'text-red-400' : 'text-cap-text-muted hover:text-red-400'}`}>
        <Heart className={`w-3 h-3 ${answer.user_liked ? 'fill-red-400' : ''}`} />
        {answer.likes_count > 0 && <span>{answer.likes_count}</span>}
      </button>
    </div>
  </div>
);

// ── Question card ──
const QuestionCard = ({ question, onDelete, onXpChange }) => {
  const [expanded, setExpanded] = useState(false);
  const [answers, setAnswers] = useState([]);
  const [loadingAnswers, setLoadingAnswers] = useState(false);
  const [newAnswer, setNewAnswer] = useState('');
  const [sending, setSending] = useState(false);

  const loadAnswers = useCallback(async () => {
    setLoadingAnswers(true);
    try { setAnswers(await communityApi.getAnswers(question.id)); } catch { /* */ }
    finally { setLoadingAnswers(false); }
  }, [question.id]);

  useEffect(() => { if (expanded) loadAnswers(); }, [expanded, loadAnswers]);

  const handleSubmitAnswer = async () => {
    if (!newAnswer.trim()) return;
    setSending(true);
    try {
      const a = await communityApi.addAnswer(question.id, newAnswer.trim());
      setAnswers(prev => [...prev, a]);
      setNewAnswer('');
      onXpChange?.(10);
      toast.success('+15 XP — Réponse ajoutée !');
    } catch { toast.error("Erreur"); }
    finally { setSending(false); }
  };

  const handleLikeAnswer = async (answerId) => {
    try {
      const r = await communityApi.toggleAnswerLike(answerId);
      setAnswers(prev => prev.map(a => a.id === answerId ? { ...a, likes_count: r.likes_count, user_liked: r.liked } : a));
    } catch { /* */ }
  };

  return (
    <div className="bg-[#1a1d27] border border-white/5 rounded-xl p-5" data-testid={`question-${question.id}`}>
      <div className="flex items-start gap-3">
        <MiniAvatar username={question.author} />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span className="text-white text-sm font-semibold">{question.author}</span>
            {question.is_owner && <span className="text-[8px] px-1 py-0.5 rounded bg-[#00e5a0]/10 text-[#00e5a0] font-bold">VOUS</span>}
            <span className="text-cap-text-muted text-[10px]">{timeSince(question.created_at)}</span>
          </div>
          <p className="text-white text-sm mb-2">{question.content}</p>
          <div className="flex items-center gap-2 flex-wrap">
            {question.symbol && <span className="font-mono text-[#00e5a0] text-xs bg-[#00e5a0]/10 px-2 py-0.5 rounded">${question.symbol}</span>}
            {question.tag && <span className="text-[10px] px-2 py-0.5 rounded-full bg-white/5 text-cap-text-secondary">#{question.tag}</span>}
          </div>
        </div>
        <div className="flex items-center gap-2">
          {question.is_owner && (
            <button onClick={() => onDelete(question.id)} className="text-cap-text-muted hover:text-red-400 p-1"><Trash2 className="w-3.5 h-3.5" /></button>
          )}
        </div>
      </div>
      {/* Toggle answers */}
      <div className="flex items-center justify-between mt-3 pt-3 border-t border-white/5">
        <button onClick={() => setExpanded(!expanded)} className="flex items-center gap-1.5 text-xs text-cap-text-secondary hover:text-[#4f8eff] transition-colors" data-testid={`toggle-answers-${question.id}`}>
          <MessageCircle className="w-4 h-4" /> {question.answers_count || 0} réponse{(question.answers_count || 0) !== 1 ? 's' : ''}
          {expanded ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
        </button>
        {!expanded && <button onClick={() => setExpanded(true)} className="text-xs text-[#00e5a0] hover:underline">Répondre</button>}
      </div>
      {expanded && (
        <div className="mt-3 space-y-1">
          {loadingAnswers ? <div className="flex justify-center py-3"><Loader2 className="w-4 h-4 animate-spin text-cap-text-muted" /></div> : (
            answers.length === 0 ? <p className="text-cap-text-muted text-xs text-center py-2">Aucune réponse encore.</p> :
              answers.map(a => <AnswerRow key={a.id} answer={a} onLike={handleLikeAnswer} />)
          )}
          <div className="flex items-center gap-2 mt-2 pt-2 border-t border-white/5">
            <input className="flex-1 bg-[#0d0f14] border border-white/10 rounded-lg px-3 h-9 text-white text-sm outline-none placeholder:text-cap-text-muted focus:border-[#00e5a0]/40"
              placeholder="Votre réponse..." value={newAnswer} onChange={e => setNewAnswer(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleSubmitAnswer()} />
            <button onClick={handleSubmitAnswer} disabled={!newAnswer.trim() || sending}
              className="h-9 px-3 rounded-lg bg-[#00e5a0] text-[#0d0f14] text-xs font-semibold disabled:opacity-40 flex items-center gap-1"
              data-testid={`submit-answer-${question.id}`}>
              {sending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

// ══════════════════════════════════════
// MAIN Q&A TAB
// ══════════════════════════════════════
const QATab = ({ onXpChange }) => {
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sort, setSort] = useState('recent');
  const [showForm, setShowForm] = useState(false);
  const [formContent, setFormContent] = useState('');
  const [formSymbol, setFormSymbol] = useState('');
  const [formTag, setFormTag] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const load = useCallback(async () => {
    try { setQuestions(await communityApi.listQuestions(sort)); }
    catch { /* */ } finally { setLoading(false); }
  }, [sort]);

  useEffect(() => { load(); }, [load]);

  const handleSubmit = async () => {
    if (!formContent.trim() || formContent.trim().length > 200) return;
    setSubmitting(true);
    try {
      const q = await communityApi.createQuestion({
        content: formContent.trim(),
        symbol: formSymbol.trim().replace('$', '').toUpperCase() || null,
        tag: formTag || null,
      });
      setQuestions(prev => [q, ...prev]);
      setFormContent(''); setFormSymbol(''); setFormTag(''); setShowForm(false);
      onXpChange?.(10);
      toast.success('+10 XP — Question publiée !');
    } catch { toast.error("Erreur"); }
    finally { setSubmitting(false); }
  };

  const handleDelete = async (id) => {
    try { await communityApi.deleteQuestion(id); setQuestions(prev => prev.filter(q => q.id !== id)); toast.success('Question supprimée'); }
    catch { toast.error("Erreur"); }
  };

  const SORT_OPTIONS = [
    { key: 'recent', label: 'Plus récentes' },
    { key: 'answers', label: 'Plus de réponses' },
    { key: 'unanswered', label: 'Sans réponse' },
  ];

  return (
    <div className="space-y-4" data-testid="qa-tab">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          {SORT_OPTIONS.map(s => (
            <button key={s.key} onClick={() => setSort(s.key)}
              className={`text-xs px-3 py-1.5 rounded-full transition-all ${sort === s.key ? 'bg-[#00e5a0]/20 text-[#00e5a0] font-bold' : 'bg-white/5 text-cap-text-secondary hover:bg-white/10'}`}>
              {s.label}
            </button>
          ))}
        </div>
        <button onClick={() => setShowForm(!showForm)} className="bg-[#00e5a0] text-[#0d0f14] font-semibold text-xs h-8 px-4 rounded-lg flex items-center gap-1.5" data-testid="ask-question-btn">
          <HelpCircle className="w-3.5 h-3.5" /> Poser une question <span className="opacity-70">+10 XP</span>
        </button>
      </div>

      {/* Form */}
      {showForm && (
        <div className="bg-[#141720] border border-[#00e5a0]/20 rounded-xl p-5 space-y-3" data-testid="qa-form">
          <div>
            <label className="block text-xs text-cap-text-secondary mb-1">Question <span className="text-red-400">*</span> <span className="text-cap-text-muted">({formContent.length}/200)</span></label>
            <textarea className="w-full bg-[#0d0f14] border border-white/10 rounded-lg px-3 py-2 text-white text-sm outline-none resize-none h-20 placeholder:text-cap-text-muted focus:border-[#00e5a0]/40"
              placeholder="Votre question à la communauté..." value={formContent} onChange={e => setFormContent(e.target.value.slice(0, 200))} />
          </div>
          <div className="flex items-center gap-3">
            <div className="flex-1">
              <label className="block text-xs text-cap-text-muted mb-1">Ticker (optionnel)</label>
              <input className="w-full bg-[#0d0f14] border border-white/10 rounded-lg px-3 h-8 text-white text-xs outline-none placeholder:text-cap-text-muted"
                placeholder="Ex: AAPL" value={formSymbol} onChange={e => setFormSymbol(e.target.value)} />
            </div>
            <div className="flex-1">
              <label className="block text-xs text-cap-text-muted mb-1">Tag (optionnel)</label>
              <select className="w-full bg-[#0d0f14] border border-white/10 rounded-lg px-3 h-8 text-white text-xs outline-none" value={formTag} onChange={e => setFormTag(e.target.value)} data-testid="qa-tag-select">
                <option value="">Aucun</option>
                <option value="action">Action</option>
                <option value="etf">ETF</option>
                <option value="crypto">Crypto</option>
                <option value="immo">Immo</option>
                <option value="epargne">Épargne</option>
                <option value="actualites">Actualités</option>
              </select>
            </div>
          </div>
          <div className="flex items-center justify-end gap-2">
            <button onClick={() => setShowForm(false)} className="text-cap-text-secondary text-xs h-8 px-4">Annuler</button>
            <button onClick={handleSubmit} disabled={!formContent.trim() || submitting}
              className="bg-[#00e5a0] text-[#0d0f14] font-semibold text-xs h-8 px-5 rounded-lg disabled:opacity-40 flex items-center gap-1"
              data-testid="submit-question-btn">
              {submitting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />} Publier
            </button>
          </div>
        </div>
      )}

      {/* Loading */}
      {loading && <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-cap-text-muted" /></div>}

      {/* Questions list */}
      {!loading && questions.length === 0 && <p className="text-cap-text-muted text-sm text-center py-12">Aucune question. Soyez le premier à poser une question !</p>}
      <div className="space-y-5">
        {questions.map(q => <QuestionCard key={q.id} question={q} onDelete={handleDelete} onXpChange={onXpChange} />)}
      </div>
    </div>
  );
};

export default QATab;
